import { Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import Icon from "../../lib/components/custom/Icon";
import LanguageToggle from "../../lib/components/custom/LanguageToggle";
import { Button } from "../../lib/components/ui/button";
import { Card } from "../../lib/components/ui/card";
import Layout from "../layout";

export default function Support() {
	const { t } = useTranslation();

	const faqKeys = ["q1", "q2", "q3", "q4", "q5", "q6", "q7", "q8"];

	return (
		<Layout>
			<div className="p-4 md:p-8 border-none md:border rounded-none md:rounded-large bg-gradient-main">
				<div className="max-w-6xl mx-auto">
					{/* Header */}
					<motion.div
						initial={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.25, ease: "easeOut" }}
						className="text-center mb-8 md:mb-12"
					>
						<h1 className="text-2xl md:text-3xl font-manrope font-semibold mb-3 md:mb-4">
							{t("support.header")}
						</h1>
						<p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto">
							{t("support.subheader")}
						</p>
					</motion.div>

					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.25, delay: 0.05, ease: "easeOut" }}
					>
						<Card className="p-4 md:p-6 mb-8 md:mb-12">
							<h2 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">
								{t("support.contact.header")}
							</h2>
							<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
								{/* Contact Information */}
								<div className="space-y-4">
									<div className="flex items-start gap-3">
										<Icon
											name="MapPin"
											className="w-5 h-5 text-primary mt-1 shrink-0"
										/>
										<div>
											<div className="font-medium">
												{t("support.contact.address_label")}
											</div>
											<div className="text-sm text-muted-foreground">
												Sandstr. 74
												<br />
												45473 Mülheim/Ruhr
											</div>
										</div>
									</div>

									<div className="flex items-center gap-3">
										<Icon name="Phone" className="w-5 h-5 text-primary" />
										<div>
											<div className="font-medium">
												{t("support.contact.phone_label")}
											</div>
											<div className="text-sm text-muted-foreground">
												0208 377 26 36
											</div>
										</div>
									</div>

									<div className="flex items-center gap-3">
										<Icon name="Mail" className="w-5 h-5 text-primary" />
										<div>
											<div className="font-medium">
												{t("support.contact.email_label")}
											</div>
											<div className="text-sm text-muted-foreground">
												americanwaypizza@gmail.com
												<br />
												{t("support.contact.email_response")}
											</div>
										</div>
									</div>

									<div className="flex items-start gap-3">
										<Icon
											name="Clock"
											className="w-5 h-5 text-primary mt-1 shrink-0"
										/>
										<div>
											<div className="font-medium">
												{t("support.contact.hours_label")}
											</div>
											<div className="text-sm text-muted-foreground">
												{t("support.contact.hours_weekday")}
												<br />
												{t("support.contact.hours_weekday_time")}
												<br />
												{t("support.contact.hours_weekend")}
												<br />
												{t("support.contact.hours_weekend_time")}
											</div>
										</div>
									</div>
								</div>

								{/* Interactive Map */}
								<div>
									<div className="h-64 rounded-large border-4 overflow-hidden mb-4">
										<iframe
											src="https://maps.google.com/maps?q=Sandstr+74,+45473+Mülheim+Ruhr,+Germany&output=embed"
											width="100%"
											height="100%"
											style={{ border: 0 }}
											allowFullScreen
											loading="lazy"
											referrerPolicy="no-referrer-when-downgrade"
											title={t("support.contact.map_title")}
										></iframe>
									</div>
									<Button variant="primary" className="w-full" asChild>
										<a
											href="https://www.google.com/maps/dir/?api=1&destination=Sandstr.+74,+45473+Mülheim+Ruhr,+Germany"
											target="_blank"
											rel="noopener noreferrer"
										>
											<Icon name="Navigation" className="w-4 h-4 mr-2" />
											{t("support.contact.get_directions")}
										</a>
									</Button>
								</div>
							</div>
						</Card>
					</motion.div>

					{/* FAQ Section */}
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.25, delay: 0.15, ease: "easeOut" }}
					>
						<Card className="p-4 md:p-6">
							<h2 className="text-xl md:text-2xl font-semibold mb-4 md:mb-6">
								{t("support.faq.header")}
							</h2>
							<div className="space-y-4">
								{faqKeys.map((key, index) => (
									<motion.details
										key={key}
										className="group border-b border-border pb-4 last:border-b-0"
										initial={{ opacity: 0 }}
										animate={{ opacity: 1 }}
										transition={{
											delay: 0.05 * index,
											duration: 0.2,
											ease: "easeOut",
										}}
									>
										<summary className="flex items-center justify-between cursor-pointer py-2 font-medium hover:text-primary transition-colors">
											<span>{t(`support.faq.${key}.question`)}</span>
											<Icon
												name="ChevronDown"
												className="w-4 h-4 transition-transform group-open:rotate-180"
											/>
										</summary>
										<motion.div
											initial={{ opacity: 0, height: 0 }}
											whileInView={{ opacity: 1, height: "auto" }}
											transition={{ duration: 0.15, ease: "easeOut" }}
											className="mt-2 text-muted-foreground pl-4 border-l-2 border-primary/20"
										>
											{t(`support.faq.${key}.answer`)}
										</motion.div>
									</motion.details>
								))}
							</div>
						</Card>
					</motion.div>
				</div>
			</div>
		</Layout>
	);
}
